import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Modal from 'react-modal';
import moment from 'moment';

import {
    TBookingGroup,
    TExtraRate,
    TGenerateTimesheetPayload,
    TSelectedBookingSummary,
    TTimesheetBooking,
} from './types';
import { createGroupKey, validateTimesheetGroup } from './utils';
import { TimesheetHeader } from './TimesheetHeader';
import { TimesheetNavigation } from './TimesheetNavigation';
import { TimesheetOverview } from './TimesheetOverview';
import { TimesheetDescription } from './TimesheetDescription';
import { BookingCard } from './BookingCard';
import { ExtraRatesSection } from './ExtraRatesSection';
import { TimesheetFooter } from './TimesheetFooter';

interface GenerateTimesheetModalProps {
    isOpen: boolean;
    onClose: () => void;
    onGenerate: (payload: TGenerateTimesheetPayload) => Promise<void>;
    bookings: TSelectedBookingSummary[];
}

const groupBookings = (
    jobs: TSelectedBookingSummary[],
): TBookingGroup[] => {
    const groups: TBookingGroup[] = [];
    const groupMap: Record<string, TBookingGroup> = {};

    jobs.forEach(summary => {
        const { freelancer, client, agent, booking, title, agency, extraRates, employerReference } = summary;
        
        const key = createGroupKey(freelancer.id, client.id);
        const bookingDetails: TTimesheetBooking = {
            ...booking,
        };
        if (!groupMap[key]) {
            groupMap[key] = {
                freelancer,
                client,
                agent,
                bookings: [],
                title,
                agency,
                extraRates: [],
                employerReference,
            };
            groups.push(groupMap[key]);
        }
        const group = groupMap[key];
        group.bookings.push(bookingDetails);
        if (extraRates?.length) {
            group.extraRates = group.extraRates.concat(extraRates);
        }
    });

    return groups;
};

const modalStyles = {
    content: {
        position: 'absolute' as const,
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        width: '45vw',
        minWidth: '640px',
        maxWidth: '900px',
        maxHeight: '85vh',
        padding: 0,
        border: 'none',
        borderRadius: '16px',
        overflow: 'hidden',
    },
    overlay: {
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        zIndex: 1000,
    },
};

export const GenerateTimesheetModal: React.FC<GenerateTimesheetModalProps> = ({
    isOpen,
    onClose,
    onGenerate,
    bookings,
}) => {
    const groupedBookings = useMemo(
        () => groupBookings(bookings),
        [bookings],
    );
    const [groups, setGroups] = useState<TBookingGroup[]>(groupedBookings);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [isEditing, setIsEditing] = useState(false);
    const [isGenerating, setIsGenerating] = useState(false);
    const [descriptions, setDescriptions] = useState<Record<string, string>>({});

    useEffect(() => {
        if (isOpen) {
            setGroups(groupedBookings);
            setCurrentIndex(0);
            setDescriptions({});
            setIsEditing(false);
            setIsGenerating(false);
        }
    }, [isOpen, groupedBookings]);

    const hasGroups = groups.length > 0;
    const currentGroup = hasGroups ? groups[currentIndex] : null;

    const {
        groupKey,
        currentDescription,
        currentTimesheetDate,
        currentPersonnelReference,
        currentClientReference,
        currentManagingDivision,
        currentManagingDepartment,
        currentClientId,
        agentName,
        agentId,
        agentConsultantCode,
        groupExtraRates,
        employerReference,
    } = useMemo(() => {
        if (!currentGroup) {
            return {
                groupKey: '',
                currentDescription: '',
                currentTimesheetDate: '',
                currentPersonnelReference: '',
                currentClientReference: '',
                currentManagingDivision: '',
                currentManagingDepartment: '',
                currentClientId: 0,
                agentName: '',
                agentId: undefined as number | undefined,
                agentConsultantCode: '',
                groupExtraRates: [],
                employerReference: '',
            };
        }

        const key = createGroupKey(
            currentGroup.freelancer.id,
            currentGroup.client.id,
        );
        const hasManualDescription = Object.prototype.hasOwnProperty.call(
            descriptions,
            key,
        );
        const resolvedDescription = hasManualDescription
            ? descriptions[key]
            : currentGroup.title;
        const bookingWithDate = currentGroup.bookings.find(entry => !!entry.date);
        const timesheetDate = bookingWithDate
            ? moment(bookingWithDate.date).endOf('isoWeek').format('YYYY-MM-DD')
            : '';

        return {
            groupKey: key,
            currentDescription: resolvedDescription,
            currentTimesheetDate: timesheetDate,
            currentPersonnelReference: currentGroup.freelancer.tempestPayrollNumber || '',
            currentClientReference: currentGroup.client.referenceNumber || '',
            currentManagingDivision: currentGroup.agency.division,
            currentManagingDepartment: currentGroup.agency.department,
            currentClientId: currentGroup.client.id,
            agentName: currentGroup.agent?.name ?? '',
            agentId: currentGroup.agent?.id,
            agentConsultantCode: currentGroup.agent?.consultantCode ?? '',
            groupExtraRates: currentGroup.extraRates ?? [],
            employerReference: currentGroup.employerReference,
        };
    }, [currentGroup, descriptions]);

    const descriptionInputId = groupKey
        ? `timesheet-description-${groupKey}`
        : 'timesheet-description';

    const validationResult = useMemo(() => validateTimesheetGroup(
            currentGroup,
            currentDescription,
            agentId,
            agentName,
            agentConsultantCode,
            currentTimesheetDate,
            currentPersonnelReference,
            currentClientReference,
            currentManagingDivision,
            currentManagingDepartment,
        ), [
        currentGroup,
        currentDescription,
        agentId,
        agentName,
        agentConsultantCode,
        currentTimesheetDate,
        currentPersonnelReference,
        currentClientReference,
        currentManagingDivision,
        currentManagingDepartment,
    ]);

    const isCurrentGroupComplete = validationResult.isValid;
    const isGenerateDisabled = !hasGroups || !isCurrentGroupComplete;
    const disabledTooltip = isGenerateDisabled
        ? `Missing: ${validationResult.errors.join(', ')}`
        : undefined;

    const isGenerationBlocked = useMemo(
        () =>
            isGenerating ||
            !currentGroup ||
            isGenerateDisabled ||
            !currentTimesheetDate ||
            !currentPersonnelReference ||
            !currentClientReference ||
            !currentManagingDivision ||
            !currentManagingDepartment ||
            !currentDescription.trim() ||
            agentId === undefined,
        [
            isGenerating,
            currentGroup,
            isGenerateDisabled,
            currentTimesheetDate,
            currentPersonnelReference,
            currentClientReference,
            currentManagingDivision,
            currentManagingDepartment,
            currentDescription,
            agentId,
        ],
    );

    const updateBookingField = useCallback(
        (
            bookingId: number,
            updater: (booking: TTimesheetBooking) => TTimesheetBooking,
        ) => {
            setGroups(prev =>
                prev.map((group, index) => {
                    if (index !== currentIndex) {
                        return group;
                    }
                    return {
                        ...group,
                        bookings: group.bookings.map(b =>
                            b.id === bookingId ? updater(b) : b,
                        ),
                    };
                }),
            );
        },
        [currentIndex],
    );

    const updateExtraRateField = useCallback(
        (
            rateId: number,
            updater: (rate: TExtraRate) => TExtraRate,
        ) => {
            setGroups(prev =>
                prev.map((group, index) => {
                    if (index !== currentIndex) {
                        return group;
                    }
                    return {
                        ...group,
                        extraRates: group.extraRates.map(r =>
                            r.id === rateId ? updater(r) : r,
                        ),
                    };
                }),
            );
        },
        [currentIndex],
    );

    const movePrev = useCallback(() => {
        setCurrentIndex(prev => Math.max(0, prev - 1));
    }, []);

    const moveNext = useCallback(() => {
        setCurrentIndex(prev => Math.min(groups.length - 1, prev + 1));
    }, [groups.length]);

    const handleToggleEdit = useCallback(() => {
        setIsEditing(prev => !prev);
    }, []);

    const handleDescriptionChange = useCallback((value: string) => {
        setDescriptions(prev => ({
            ...prev,
            [groupKey]: value,
        }));
    }, [groupKey]);

    const handleGenerateCurrent = useCallback(async() => {
        if (isGenerationBlocked) {
            return;
        }

        setIsGenerating(true);

        try {
            const bookingIds = currentGroup.bookings.map(entry => entry.id);
            
            // Add bookings to pay_rate (adhoc_pay_rate: false)
            const bookingPayRates = currentGroup.bookings.map(entry => ({
                duration: entry.duration,
                hourly_rate: entry.freelancerPayRate,
                client_pay: entry.clientPayRate ?? 0,
                rate_code: entry.rateCode?.trim() || '',
                description: entry.rateDescription?.trim() || '',
                adhoc_pay_rate: false,
            }));

            // Add extra rates to pay_rate (adhoc_pay_rate: true)
            const extraPayRates = currentGroup.extraRates.map(rate => ({
                duration: rate.duration,
                hourly_rate: rate.freelancerPayPerHour,
                client_pay: rate.clientPayPerHour,
                rate_code: rate.extraRateCode?.trim() || '',
                description: rate.description?.trim() || '',
                adhoc_pay_rate: true,
            }));

            const payRatePayload = [...bookingPayRates, ...extraPayRates];

            // Wait for API response
            await onGenerate({
                freelancer: currentGroup.freelancer.id,
                client: currentClientId,
                agent: agentId,
                timesheet_date: currentTimesheetDate,
                personnel_reference: currentPersonnelReference,
                employer_reference: employerReference,
                client_reference: currentClientReference,
                consultant_code: agentConsultantCode,
                division: currentManagingDivision,
                department: currentManagingDepartment,
                description: currentDescription.trim(),
                bookings: bookingIds,
                pay_rate: payRatePayload,
            });

            // Only remove group if API call was successful
            const remainingGroups = groups.filter(
                (_, index) => index !== currentIndex,
            );
            if (remainingGroups.length === 0) {
                setGroups([]);
                setCurrentIndex(0);
                onClose();
                return;
            }
            setGroups(remainingGroups);
            setCurrentIndex(prev =>
                prev >= remainingGroups.length ? remainingGroups.length - 1 : prev,
            );
        } catch (error) {
            // Error is already handled by parent, just keep the group open
            console.error('Failed to generate timesheet:', error);
        } finally {
            setIsGenerating(false);
        }
    }, [
        isGenerationBlocked,
        currentGroup,
        currentClientId,
        agentId,
        currentTimesheetDate,
        currentPersonnelReference,
        employerReference,
        currentClientReference,
        agentConsultantCode,
        currentManagingDivision,
        currentManagingDepartment,
        currentDescription,
        onGenerate,
        groups,
        currentIndex,
        onClose,
    ]);

    return (
        <Modal
            className="timesheet-modal"
            isOpen={isOpen}
            onRequestClose={onClose}
            contentLabel="Generate Timesheet"
            style={modalStyles}
            ariaHideApp={false}
        >
            <div className="timesheet-modal__container">
                <TimesheetHeader onClose={onClose} />
                <div className="timesheet-modal__body">
                    {!hasGroups ? (
                        <p>No bookings selected.</p>
                    ) : (
                        <>
                            <TimesheetNavigation
                                currentIndex={currentIndex}
                                totalGroups={groups.length}
                                onPrevious={movePrev}
                                onNext={moveNext}
                            />
                            <div className="timesheet-modal-table-wrapper">
                                <div className="timesheet-group">
                                    <div className="timesheet-group-header">
                                        <TimesheetOverview
                                            freelancerName={currentGroup?.freelancer.name || ''}
                                            clientName={currentGroup?.client.name || ''}
                                            agentName={agentName}
                                            agentConsultantCode={agentConsultantCode}
                                            timesheetDate={currentTimesheetDate}
                                            personnelReference={currentPersonnelReference}
                                            employerReference={employerReference}
                                            clientReference={currentClientReference}
                                            division={currentManagingDivision}
                                            department={currentManagingDepartment}
                                        />
                                        <TimesheetDescription
                                            description={currentDescription}
                                            isEditing={isEditing}
                                            inputId={descriptionInputId}
                                            onChange={handleDescriptionChange}
                                        />
                                        <div className="timesheet-booking-cards">
                                            {currentGroup?.bookings.map(booking => (
                                                <BookingCard
                                                    key={booking.id}
                                                    booking={booking}
                                                    isEditing={isEditing}
                                                    onUpdateBooking={updateBookingField}
                                                />
                                            ))}
                                        </div>
                                        <ExtraRatesSection
                                            extraRates={groupExtraRates}
                                            isEditing={isEditing}
                                            onUpdateRate={updateExtraRateField}
                                        />
                                    </div>
                                </div>
                            </div>
                        </>
                    )}
                </div>
                <TimesheetFooter
                    isEditing={isEditing}
                    isGenerateDisabled={isGenerateDisabled}
                    isGenerating={isGenerating}
                    disabledTooltip={disabledTooltip}
                    onToggleEdit={handleToggleEdit}
                    onGenerate={handleGenerateCurrent}
                    onCancel={onClose}
                />
            </div>
        </Modal>
    );
};
